/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import Add from '@mui/icons-material/Add';
import MoreHoriz from '@mui/icons-material/MoreHoriz';

import { Button, ButtonGroup, SortButton } from 'uikit/buttons';
import { Checkbox } from 'uikit/inputs';
import { Header } from 'uikit/layouts';
import { Label } from 'uikit/labels';
import { Menu } from 'uikit/menus';
import { Typo } from 'uikit/content';

import Filters from './filters';
import MenuBreadcrumbs from './menuBreadcrumbs';
import Notice from './notice';
import ToggleButtons from './toggleButtons';

import styles from '../styles/listHeader.module.scss';

const ListHeader = ({
  addMenuItems = [],
  breadcrumbs = [],
  buttons = [],
  buttonsHelperText = '',
  checkboxInputAriaLabel = '',
  checkboxValue = false,
  className = '',
  columns = [],
  errorText = '',
  filters = [],
  filterSelection = {},
  hasAddMenu = false,
  hasBreadcrumbs = false,
  hasButtons = false,
  hasButtonsHelperText = false,
  hasCheckbox = false,
  hasErrorText = false,
  hasFilters = false,
  hasGrowerColumn = true,
  hasLabel = false,
  hasMoreMenu = false,
  hasNotice = false,
  hasSort = false,
  hasTitleHeader = true,
  hasToggleButtons = false,
  isCheckboxDisabled = false,
  isCheckboxIndeterminate = false,
  isDisabled = false,
  isSortDisabled = false,
  labelIcon = null,
  labelText = '',
  lastBreadcrumbMenuItems = [],
  moreMenuItems = [],
  nameMetaText = '',
  nameText = i18n.t('name'),
  noticeButtonText = '',
  noticeOnClick = () => {},
  noticeText = '',
  onCheckboxChange = () => {},
  onClearButtonClick = () => {},
  onFilterChange = () => {},
  onSortClick = () => {},
  onToggleButtonsChange = () => {},
  rowClassName = '',
  shouldDisableFilters = false,
  sortDirection = 'asc',
  sortKey = '',
  testId = '',
  title = '',
  toggleButtons = [],
  toggleButtonsAriaLabel = '',
  toggleButtonsColor = 'primary',
  toggleButtonsValue = null,
}) => (
  <>
    {hasLabel && <Label icon={labelIcon} text={labelText} type="brand" variant="s01" />}
    {hasTitleHeader && (
      <Header
        className={classnames(styles.element, styles.isTitle, className, {
          [styles.hasNotice]: hasNotice,
        })}
        leftColumn={
          <div className={styles.leftColumn}>
            <div className={styles.titleContainer}>
              <Typo className={styles.title} element="h2" variant="d01">
                {title}
              </Typo>
              {hasAddMenu && (
                <Menu
                  className={styles.menu}
                  component={
                    <Button
                      ariaLabel={i18n.t('add')}
                      icon={<Add />}
                      size="small"
                      variant="outlined"
                      isDisabled={isDisabled}
                      aria-disabled={isDisabled}
                    />
                  }
                  horizontalPosition="left"
                >
                  {addMenuItems}
                </Menu>
              )}
              {hasButtons && (
                <ButtonGroup className={styles.buttons}>
                  {buttons.map((button) => (
                    <Button key={button.id ?? button.text} {...button} />
                  ))}
                  {hasMoreMenu && (
                    <Menu
                      component={
                        <Button
                          ariaLabel={i18n.t('more')}
                          icon={<MoreHoriz />}
                          size="small"
                          variant="outlined"
                        />
                      }
                      horizontalPosition="left"
                    >
                      {moreMenuItems}
                    </Menu>
                  )}
                </ButtonGroup>
              )}
              <div className={styles.textWrap}>
                {hasButtonsHelperText && (
                  <Typo
                    className={styles.buttonsHelperText}
                    element="span"
                    variant="b01"
                    weight="medium"
                  >
                    {buttonsHelperText}
                  </Typo>
                )}
                {hasErrorText && (
                  <Typo className={styles.errorText} element="span" variant="s01" weight="medium">
                    {errorText}
                  </Typo>
                )}
              </div>
            </div>
            {hasBreadcrumbs && (
              <MenuBreadcrumbs
                breadcrumbs={breadcrumbs}
                lastBreadcrumbMenuItems={lastBreadcrumbMenuItems}
              />
            )}
            {hasNotice && (
              <Notice
                className={styles.notice}
                buttonText={noticeButtonText}
                hasButton
                onClick={noticeOnClick}
                testId="list-header-notice"
                text={noticeText}
              />
            )}
          </div>
        }
        rightColumn={
          <>
            {hasFilters && (
              <Filters
                filters={filters}
                isDisabled={shouldDisableFilters}
                onClearButtonClick={onClearButtonClick}
                onSelectionUpdate={onFilterChange}
                selection={filterSelection}
              />
            )}
            {hasToggleButtons && (
              <ToggleButtons
                ariaLabel={toggleButtonsAriaLabel}
                buttons={toggleButtons}
                color={toggleButtonsColor}
                isDisabled={isDisabled}
                onChange={onToggleButtonsChange}
                value={toggleButtonsValue}
              />
            )}
          </>
        }
        testId={testId}
      />
    )}
    <div className={classnames(styles.columns, rowClassName)}>
      {hasCheckbox && (
        <Checkbox
          id="list-header-checkbox"
          inputAriaLabel={checkboxInputAriaLabel}
          inputTestId="list-header-checkbox-input"
          isIndeterminate={isCheckboxIndeterminate}
          isDisabled={isCheckboxDisabled}
          onChange={onCheckboxChange}
          testId="list-header-checkbox"
          value={checkboxValue}
        />
      )}
      {hasGrowerColumn && (
        <Typo className={classnames(styles.column, styles.isGrower)} variant="s01" weight="medium">
          {nameText}
          {nameMetaText && (
            <Typo className={styles.metaText} element="span" variant="s01">
              {nameMetaText}
            </Typo>
          )}
        </Typo>
      )}
      {columns.map((column) => {
        const { key, label = column.label || column } = column;

        return (
          <Typo
            className={classnames(styles.column, styles.isFixed)}
            element="div"
            key={label}
            variant="s01"
            weight="medium"
          >
            {label}
            {hasSort && (
              <SortButton
                isDisabled={isSortDisabled}
                onClick={(direction) => onSortClick({ direction, key })}
                sort={key === sortKey ? sortDirection : 'unsorted'}
              />
            )}
          </Typo>
        );
      })}
      <div className={styles.columnEnd} />
    </div>
  </>
);

ListHeader.propTypes = {
  addMenuItems: PropTypes.node,
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({})),
  buttons: PropTypes.arrayOf(PropTypes.shape({})),
  buttonsHelperText: PropTypes.string,
  checkboxInputAriaLabel: PropTypes.string,
  checkboxValue: PropTypes.bool,
  className: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
      }),
    ]),
  ),
  errorText: PropTypes.string,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      allow_multiple_selects: PropTypes.bool,
      key: PropTypes.string,
      label: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
        }),
      ),
    }),
  ),
  filterSelection: PropTypes.shape({}),
  hasAddMenu: PropTypes.bool,
  hasButtons: PropTypes.bool,
  hasButtonsHelperText: PropTypes.bool,
  hasBreadcrumbs: PropTypes.bool,
  hasCheckbox: PropTypes.bool,
  hasErrorText: PropTypes.bool,
  hasFilters: PropTypes.bool,
  hasGrowerColumn: PropTypes.bool,
  hasLabel: PropTypes.bool,
  hasMoreMenu: PropTypes.bool,
  hasNotice: PropTypes.bool,
  hasSort: PropTypes.bool,
  hasTitleHeader: PropTypes.bool,
  hasToggleButtons: PropTypes.bool,
  isCheckboxDisabled: PropTypes.bool,
  isCheckboxIndeterminate: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSortDisabled: PropTypes.bool,
  labelIcon: PropTypes.node,
  labelText: PropTypes.string,
  lastBreadcrumbMenuItems: PropTypes.arrayOf(PropTypes.shape({})),
  moreMenuItems: PropTypes.node,
  nameText: PropTypes.string,
  nameMetaText: PropTypes.string,
  noticeButtonText: PropTypes.string,
  noticeOnClick: PropTypes.func,
  noticeText: PropTypes.string,
  onCheckboxChange: PropTypes.func,
  onClearButtonClick: PropTypes.func,
  onFilterChange: PropTypes.func,
  onSortClick: PropTypes.func,
  onToggleButtonsChange: PropTypes.func,
  rowClassName: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  shouldDisableFilters: PropTypes.bool,
  sortDirection: PropTypes.oneOf(['asc', 'desc', 'unsorted']),
  sortKey: PropTypes.string,
  testId: PropTypes.string,
  title: PropTypes.string,
  toggleButtons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
    }),
  ),
  toggleButtonsAriaLabel: PropTypes.string,
  toggleButtonsColor: PropTypes.oneOf(['primary', 'brand']),
  toggleButtonsValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
};

export default React.memo(ListHeader);
